/**
 * @description Serviço para criar um pet (Patient) e vinculá-lo ao tutor (RelatedPerson)
 * @module CreateVetPatientWithRelation
 *
 * Este serviço encapsula a lógica completa de cadastro de um pet:
 * 1. Cria o Patient (pet) no sistema veterinário
 * 2. Cria o RelatedPerson vinculando o pet ao tutor
 *
 * Reutilizável em diferentes partes do sistema (MyPets, Agendamento, etc)
 */

import { IPetFormData } from "@/fluxvet/components/PetFormModal";
import { IVetPatientData, IRelatedPersonData } from "@/utils/interfaces";
import { PostVetPatient } from "./PostVetPatient";
import { PostRelatedPerson } from "../relatedperson/PostRelatedPerson";
import { HTTPResponseStatus } from "@/utils/enum";
import dayjs from "dayjs";

/**
 * Interface para os dados do tutor necessários para criar o RelatedPerson
 */
export interface ITutorData {
  cpf: string;
  name: string;
  phoneNumber: string;
  gender: string;
}

/**
 * Interface para o retorno do serviço
 */
export interface ICreateVetPatientResult {
  success: boolean;
  petId?: string;
  petName?: string;
  relatedPersonId?: string;
  error?: string;
}

/**
 * Cria um pet (Patient) e vincula ao tutor (RelatedPerson)
 *
 * @param petFormData - Dados do formulário do pet
 * @param tutorData - Dados do tutor (CPF, nome, telefone, gênero)
 * @returns Resultado da operação com IDs criados
 */
export async function CreateVetPatientWithRelation(
  petFormData: IPetFormData,
  tutorData: ITutorData
): Promise<ICreateVetPatientResult> {
  try {
    // Passo 1: Preparar dados do pet no formato esperado pela API
    const vetPatientData: IVetPatientData = {
      name: petFormData.name,
      gender: petFormData.sex, // sex do formulário vai para gender no Patient
      // Adiciona birthDate apenas se for válido
      ...(petFormData.birthDate &&
        dayjs(petFormData.birthDate).isValid() && {
          birthDate: petFormData.birthDate,
        }),
      // Species: extrair code e display do objeto
      speciesCode: petFormData.species?.code,
      speciesDisplay: petFormData.species?.display,
      // Breed: extrair code e display do objeto
      breedCode: petFormData.breed?.code,
      breedDisplay: petFormData.breed?.display,
      // Color: extrair code e display do objeto
      colorCode: petFormData.color?.code,
      colorDisplay: petFormData.color?.display,
      // GenderStatus: extrair code e display do objeto
      genderStatusCode: petFormData.genderStatus?.code,
      genderStatusDisplay: petFormData.genderStatus?.display,
      // Localização
      city: petFormData.city,
      state: petFormData.state?.value,
      // Não incluímos foto no fluxo de agendamento
    };

    // Passo 2: Criar o Patient (Pet)
    const petResponse = await PostVetPatient(vetPatientData);

    if (!petResponse || petResponse.status !== HTTPResponseStatus.Created) {
      return {
        success: false,
        error: "Erro ao criar o pet",
      };
    }

    const petId = petResponse.data?.id;
    const petName = petResponse.data?.name?.[0]?.text || petFormData.name;

    // Passo 3: Preparar dados do RelatedPerson para vincular pet ao tutor
    const relatedPersonData: IRelatedPersonData = {
      patient: {
        reference: `Patient/${petId}`,
        display: petName,
      },
      phoneNumber: tutorData.phoneNumber,

      ownerName: tutorData.name,
      gender: tutorData.gender,
      cpfResponsible: tutorData.cpf.replace(/[.-]/g, ""), // Remove formatação do CPF
    };

    // Passo 4: Criar o RelatedPerson vinculando pet ao tutor
    const relatedPersonResponse = await PostRelatedPerson(
      relatedPersonData,
      {
        reference: `Patient/${petId}`,
        display: petName,
      },
      true, // status = active
      true // isVetSystem = true (adiciona header X-Target-System: vet)
    );

    if (
      !relatedPersonResponse ||
      relatedPersonResponse.status !== HTTPResponseStatus.Success
    ) {
      // Pet foi criado mas falhou ao vincular ao tutor
      // Idealmente deveria fazer rollback, mas por enquanto retornamos erro
      return {
        success: false,
        petId, // Retorna o ID do pet mesmo com erro no vínculo
        error: "Pet criado, mas erro ao vincular ao tutor",
      };
    }

    // Sucesso! Pet criado e vinculado ao tutor
    return {
      success: true,
      petId,
      petName,
      relatedPersonId: relatedPersonResponse.data?.id,
    };
  } catch (error) {
    console.error("Erro em CreateVetPatientWithRelation:", error);
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Erro desconhecido ao criar pet",
    };
  }
}
